import { desplazamiento } from '../lib/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth';
import { esCuentaDeTablet, olvidarModoTablet, RUTA_SELECTOR_TABLET } from '../lib/modoTablet';
import { api, mensajeError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import FirmaDedo from '../components/FirmaDedo';
import { Loader2, CheckCircle2, Download, AlertCircle, Camera, Clock, RefreshCw } from 'lucide-react';
import ejemploFrontal from '../assets/ejemplo_foto_frontal.jpg';
import ejemploOblicua from '../assets/ejemplo_foto_oblicua.jpg';

// Las dos fotos del enlace de casa, con ejemplo (2026-08-21): van directas a
// la galería «antes» de la historia. La tablet NO pide fotos.
// Y desde el 2026-09-15 tampoco se piden cuando la CITA de este enlace es una
// revisión sucesiva o una intervención: a esos pacientes ya se les fotografió
// aquí. Lo contesta el servidor en `pide_fotos`, con la misma regla que el
// cuestionario de salud (salud_router.pide_fotos_para_cita).
const FOTOS_CASA = [
  { vista: 'frontal', titulo: '1 · De frente', ejemplo: ejemploFrontal,
    ayuda: 'Mirando a la cámara, cara centrada, buena luz.' },
  { vista: 'oblicua', titulo: '2 · Girada (45°)', ejemplo: ejemploOblicua,
    ayuda: 'Media vuelta hacia un lado, como en el ejemplo.' },
];
import { toast } from 'sonner';
import { useClinica, pieClinica } from '../lib/clinica';
import {
  NO_LO_RECUERDA, esOrigenQueYaNoExiste, etiquetaParaElPaciente, useOpcionesComoNosConocio,
} from '../lib/origenes';
import {
  useTelefonoClinica, respuestaPerdida, cuandoReintentar, aQuienLlamar, fotoRechazada,
} from '../lib/enlacePaciente';
import LogoClinica from '../components/LogoClinica';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// LA MARCA DEL ENLACE (administración, 28/10/2026). El servidor la congela en
// el enlace con la regla de todos los documentos (services/marca_documentos:
// el titular, el formato de la clínica; otro médico, el de su marca) y la
// manda en `GET /api/public/lopd/casa/{token}` como `marca`: nombre, lema,
// dirección, teléfono, web, colores y `logo_url` si tiene logotipo. Aquí no
// hay ningún dato de ninguna marca: lo que no venga, no se pinta.

/** La marca tal y como la manda el servidor, o null si no viene o no trae nombre. */
function marcaDelEnlace(m) {
  if (!m || typeof m !== 'object' || !String(m.nombre || '').trim()) return null;
  return m;
}

/** Una línea con lo que se pone al pie con marca: nombre · dirección · teléfono · web
 *  (como el pie del PDF; la razón social es de Configuración y va en el
 *  «Responsable del tratamiento», no aquí). */
export function pieMarca(m) {
  return [m.nombre, m.direccion, m.telefono, m.web].filter(Boolean).join(' · ');
}

/** El logotipo de la marca del enlace, por la puerta pública acotada al token
 *  (`logo_url`). Si no lo tiene o la imagen no carga, nada: el nombre va al lado. */
function LogoMarca({ marca, className = '' }) {
  const [fallo, setFallo] = useState(false);
  if (!marca?.logo_url || fallo) return null;
  return (
    <img
      src={`${BACKEND_URL}${marca.logo_url}`}
      alt={marca.nombre || 'Logotipo'}
      className={className}
      onError={() => setFallo(true)}
      data-testid="logo-marca"
    />
  );
}

/* LO QUE NO LE SALIÓ AL PACIENTE, AL SERVIDOR (dirección, 30/10/2026).
 *
 * Pacientes que dicen que la LOPD «les dio error» y nadie podía saber qué
 * vieron. El servidor ya apunta cada envío que rechaza (backend
 * services/lopd_intentos.py); lo que NO ve —los frenos de esta página antes
 * de mandar (falta la firma, faltan las fotos…) y los envíos sin respuesta—
 * se lo cuenta la página: un tipo de una lista cerrada y el texto que vio.
 * Fuego y olvido: si el aviso falla, el paciente no ve nada y no se
 * reintenta en bucle; el que se queda sin respuesta se guarda en esta pestaña
 * y sale con la siguiente petición buena (`AVISOS_PENDIENTES`). En casa va
 * ligado al token del enlace; en la tablet, a la sesión iniciada (la cuenta
 * del iPad o la del personal en «Modo tablet»). Nada de lo que escribió el
 * paciente, y nada que delate una respuesta de salud (`textoSinSalud`). */

/** Los nombres de los campos tal como salen en el formulario. */
const ETIQUETA_CAMPO = {
  name: 'Nombre', surname1: 'Apellido(s)', nif: 'DNI / NIE / Pasaporte',
  birth_date: 'Fecha de nacimiento', phone: 'Teléfono', calle: 'Calle y número',
  codigo_postal: 'Código Postal', poblacion: 'Ciudad', pais: 'País',
  how_known_us: '¿Cómo nos conoció?', consent_reminders: 'Consentimiento de tratamiento de datos',
};

/** Como mucho tantos avisos por paciente (el contador vuelve a cero con
 *  «Listo — siguiente paciente», que en la tablet no recarga la página):
 *  quien pulsa sin parar no convierte esto en un bucle (el servidor tiene
 *  además su freno). */
const MAX_AVISOS_POR_PAGINA = 20;
const TOPE_TEXTO_AVISO = 300;
const TITULO_NO_SE_SABE = 'No hemos podido confirmar si su formulario ha llegado';

/* NADA QUE DELATE UNA RESPUESTA DE SALUD (revisión del 30/10/2026). «Falta el
 * detalle de alergias» dice que contestó que sí, y el aviso se guarda 90 días
 * con su ficha. Al paciente se le sigue diciendo lo concreto; al servidor,
 * uno neutro. La misma regla que el servidor (lopd_intentos.motivo_sin_salud),
 * que además lo vuelve a limpiar al guardarlo. */
export const SALUD_FALTA = 'Cuestionario de salud: falta un detalle';
export const SALUD_LARGO = 'Cuestionario de salud: un texto demasiado largo';
export const SALUD_SIN_RESPUESTA = 'Cuestionario de salud: falta una respuesta';
export const SALUD_NO_VALE = 'Cuestionario de salud: un dato no válido';
const PALABRAS_SALUD = /cuestionario de salud|alergi|intoleran|medicaci|medicament|hialur[oó]nic|cirug|quir[oó]fano|mejorar o tratar|gustar[ií]a mejorar/i;

function saludNeutra(trozo) {
  const bajo = trozo.toLowerCase();
  if (bajo.includes('máximo') || bajo.includes('demasiado larg')) return SALUD_LARGO;
  if (bajo.includes('sí o no')) return SALUD_SIN_RESPUESTA;
  if (/falta|obligatorio|cuéntanos/.test(bajo)) return SALUD_FALTA;
  return SALUD_NO_VALE;
}

/** El texto con cada trozo (« · ») del cuestionario de salud cambiado por uno neutro. */
export function textoSinSalud(texto) {
  const salida = [];
  String(texto || '').split(' · ').forEach((trozo) => {
    const limpio = PALABRAS_SALUD.test(trozo) ? saludNeutra(trozo) : trozo;
    if (!salida.includes(limpio)) salida.push(limpio);
  });
  return salida.join(' · ');
}

/**
 * ¿Este error ya lo apuntó el servidor? Lo dice su cabecera
 * `X-Intento-Apuntado` (backend `lopd_intentos.CABECERA_APUNTADO`), y un 500
 * a secas solo lo da el propio servidor, que lo apunta antes de contestar (si
 * el servidor no está, el proxy contesta 502). Entonces la página no lo
 * vuelve a contar: saldría dos veces en la pantalla de LOPD.
 */
export function yaLoApuntoElServidor(e) {
  const cab = e?.response?.headers;
  let marca;
  if (cab && typeof cab.get === 'function') marca = cab.get('x-intento-apuntado');
  if (marca === undefined || marca === null) marca = cab?.['x-intento-apuntado'] ?? cab?.['X-Intento-Apuntado'];
  if (String(marca ?? '').trim() === '1') return true;
  return e?.response?.status === 500;
}

/** El aviso que se manda: el tipo, el texto (sin salud, recortado) y el
 *  código si lo hubo. */
export function cuerpoDelAviso(tipo, texto, codigo) {
  const cuerpo = { tipo, texto: textoSinSalud(texto).slice(0, TOPE_TEXTO_AVISO) };
  if (Number.isInteger(codigo) && codigo >= 100 && codigo <= 599) cuerpo.codigo = codigo;
  return cuerpo;
}

/**
 * ¿Qué fue? Con respuesta del servidor (que no lo apuntó), `error_servidor`;
 * una petición que salió y no tuvo contestación (axios: `request` sin
 * `response`), `sin_respuesta`; cualquier otra cosa, la propia página antes de
 * mandar nada (también un DOMException con `code`, p. ej. el SecurityError
 * de la firma), `error_pagina`.
 */
export function tipoDelFallo(e) {
  if (e?.response) return 'error_servidor';
  if (e?.isAxiosError || e?.request) return 'sin_respuesta';
  return 'error_pagina';
}

/* LOS AVISOS QUE SE QUEDAN SIN RESPUESTA (revisión del 30/10/2026). Justo
 * cuando la red falla —«no se sabe», «no ha llegado»— el aviso tampoco sale.
 * Se aparta en esta pestaña (sessionStorage) antes de mandarlo y se quita en
 * cuanto el servidor contesta, sea lo que sea; si no contesta, se vuelve a
 * mandar al abrir la página o tras la siguiente petición buena. Pocos, y
 * cada uno una vez por intento: nunca un bucle. Sin almacenamiento (Safari
 * privado), sale como antes y ya. */
const AVISOS_PENDIENTES = 'cp_lopd_avisos_pendientes';
const MAX_PENDIENTES = 5;
const avisosEnVuelo = new Set();

function leerPendientes() {
  try {
    const lista = JSON.parse(window.sessionStorage.getItem(AVISOS_PENDIENTES) || '[]');
    return Array.isArray(lista) ? lista.filter((a) => a && a.id && a.url && a.cuerpo) : [];
  } catch {
    return [];
  }
}

function guardarPendientes(lista) {
  try {
    if (lista.length) window.sessionStorage.setItem(AVISOS_PENDIENTES, JSON.stringify(lista.slice(-MAX_PENDIENTES)));
    else window.sessionStorage.removeItem(AVISOS_PENDIENTES);
  } catch {
    /* sin almacenamiento: el aviso sale una vez y ya */
  }
}

function quitarPendiente(id) {
  guardarPendientes(leerPendientes().filter((a) => a.id !== id));
}

/** Manda un aviso (apartado antes) y lo quita cuando el servidor contesta. */
function mandarAviso(aviso) {
  if (avisosEnVuelo.has(aviso.id)) return;
  avisosEnVuelo.add(aviso.id);
  try {
    Promise.resolve(api.post(aviso.url, aviso.cuerpo, { timeout: 15000 }))
      .then(() => {
        avisosEnVuelo.delete(aviso.id);
        quitarPendiente(aviso.id);
        reenviarAvisosPendientes();
      })
      .catch((e) => {
        avisosEnVuelo.delete(aviso.id);
        // Contestó (un 401, un 429…): reintentarlo no lo arregla.
        if (e?.response) quitarPendiente(aviso.id);
      });
  } catch {
    avisosEnVuelo.delete(aviso.id);
  }
}

/** Los avisos que se quedaron sin respuesta, otra vez (una por llamada). */
export function reenviarAvisosPendientes() {
  leerPendientes().forEach(mandarAviso);
}

/** Aparta el aviso y lo manda. */
export function avisarIntentoLopd(url, cuerpo) {
  const aviso = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, url, cuerpo };
  guardarPendientes([...leerPendientes(), aviso]);
  mandarAviso(aviso);
}

// Longitudes máximas = las del backend (models.py LOPDPublicSubmit, Address y
// SaludRespuestas), para que un 422 por texto largo no llegue a producirse
// (revisión de enlaces 2026-09-07).
const MAX = {
  name: 100, surname1: 100, nif: 20, email: 120, phone: 30,
  calle: 200, poblacion: 100, codigo_postal: 10,
  salud_text: 1000, mejorar: 2000,
};

// «¿Cómo nos conoció?» (23/9/2026, decisión de dirección): obligatorio también
// al cerrar la LOPD, con las opciones del catálogo VIGENTE del KPI «Captación
// por origen» (lo edita dirección) y «No lo recuerda» para quien de verdad no
// lo sabe. Las pide la página a la puerta pública del catálogo
// (`useOpcionesComoNosConocio({ publico: true })`, que vuelve a preguntar si
// no contesta): lo que se guarda es el valor del catálogo, y al paciente se
// le enseña entero, con el texto que manda el servidor. Facebook, YouTube y Doctoralia dejan de salir mientras
// dirección no los añada: el KPI no los cuenta aparte.
//
// LO QUE SE ENVÍA ES LO QUE SE ENSEÑÓ (segunda pasada del supervisor,
// 23/9/2026): el valor de cada opción es el texto que lee el paciente
// («Recomendación médica»), no la abreviatura del catálogo. El papel firmado
// lo guarda y lo imprime tal cual; la ficha se queda con «Rec. médica», que
// lo pone el servidor.

const COUNTRY_OPTIONS = [
  'España',
  'Portugal',
  'Francia',
  'Italia',
  'Reino Unido',
  'Alemania',
  'Otro',
];

// Phase 23.6 — Selector de fecha amigable para tablet.
// Phase 23.7 — Cambiado de Shadcn Select (Radix) a `<select>` HTML nativo:
// en Safari iPad, Radix Select tiene problemas conocidos con eventos táctiles
// dentro de portales (los SelectItem no responden al tap). El `<select>` nativo
// activa el wheel-picker rueda nativo del iOS, que es la UX ideal en tablet.
// Internamente sigue manejando un string ISO "YYYY-MM-DD" para no romper la
// integración con el backend.
const MONTHS_ES = [
  { v: '01', label: 'Enero' },
  { v: '02', label: 'Febrero' },
  { v: '03', label: 'Marzo' },
  { v: '04', label: 'Abril' },
  { v: '05', label: 'Mayo' },
  { v: '06', label: 'Junio' },
  { v: '07', label: 'Julio' },
  { v: '08', label: 'Agosto' },
  { v: '09', label: 'Septiembre' },
  { v: '10', label: 'Octubre' },
  { v: '11', label: 'Noviembre' },
  { v: '12', label: 'Diciembre' },
];
const CURRENT_YEAR = new Date().getFullYear();
const BIRTH_YEARS = Array.from({ length: 111 }, (_, i) => CURRENT_YEAR - 5 - i);
const DAYS_31 = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));

// Estilo replicando el look de un Input/SelectTrigger de Shadcn pero usando
// <select> nativo. h-12 garantiza objetivo táctil >= 44px (HIG iOS). Lo
// comparten la fecha de nacimiento, el país y «¿Cómo nos conociste?»
// (revisión de enlaces 2026-09-07: en Safari iPad el Select de Radix abría el
// menú pero al tocar una opción no pasaba nada, igual que pasó con la fecha).
function clsSelectNativo(hasError) {
  return [
    'flex w-full h-12 items-center justify-between rounded-md border bg-background',
    'px-3 py-2 text-base ring-offset-background',
    'focus:outline-none focus:ring-2 focus:ring-[#2E6E5E] focus:ring-offset-1',
    'appearance-none cursor-pointer',
    hasError ? 'border-red-500' : 'border-input',
  ].join(' ');
}

function BirthDatePicker({ value, onChange, onBlur, hasError }) {
  // Phase 23.7 fix — usamos estado LOCAL para cada parte (día/mes/año) porque
  // si derivamos del prop `value` (ISO completo) y sólo notificamos al padre
  // cuando los 3 están seleccionados, los selects intermedios "se borran" al
  // re-renderizar (el bug que reportó el usuario: el menú se abre pero al
  // tocar una opción no pasa nada, porque React lo resetea instantáneamente).
  // Inicializamos el estado local desde `value` para soportar edición posterior.
  const [parts, setParts] = useState(() => {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      return { day: d, month: m, year: y };
    }
    return { day: '', month: '', year: '' };
  });

  // Si el padre cambia el value (p.ej. reset del form), sincronizamos.
  useEffect(() => {
    if (!value) {
      setParts({ day: '', month: '', year: '' });
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      setParts((prev) =>
        prev.day === d && prev.month === m && prev.year === y ? prev : { day: d, month: m, year: y }
      );
    }
  }, [value]);

  const handleChange = (field, val) => {
    const next = { ...parts, [field]: val };
    setParts(next);
    // Notificamos al padre sólo si los 3 están completos; si falta alguno,
    // limpiamos el value del padre para que la validación marque "obligatorio".
    if (next.day && next.month && next.year) {
      onChange(`${next.year}-${next.month}-${next.day}`);
    } else {
      onChange('');
    }
  };

  const selectCls = clsSelectNativo(hasError);

  return (
    <div className="grid grid-cols-3 gap-2">
      <select
        value={parts.day}
        onChange={(e) => handleChange('day', e.target.value)}
        onBlur={onBlur}
        className={selectCls}
        data-testid="public-lopd-birth-day"
        aria-label="Día"
      >
        <option value="" disabled>Día</option>
        {DAYS_31.map(d => (
          <option key={d} value={d}>{parseInt(d, 10)}</option>
        ))}
      </select>
      <select
        value={parts.month}
        onChange={(e) => handleChange('month', e.target.value)}
        onBlur={onBlur}
        className={selectCls}
        data-testid="public-lopd-birth-month"
        aria-label="Mes"
      >
        <option value="" disabled>Mes</option>
        {MONTHS_ES.map(m => (
          <option key={m.v} value={m.v}>{m.label}</option>
        ))}
      </select>
      <select
        value={parts.year}
        onChange={(e) => handleChange('year', e.target.value)}
        onBlur={onBlur}
        className={selectCls}
        data-testid="public-lopd-birth-year"
        aria-label="Año"
      >
        <option value="" disabled>Año</option>
        {BIRTH_YEARS.map(y => (
          <option key={y} value={String(y)}>{y}</option>
        ))}
      </select>
    </div>
  );
}

function Asterisk() {
  return <span className="text-red-600 ml-0.5">*</span>;
}

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
      <AlertCircle className="w-3 h-3" /> {msg}
    </p>
  );
}

/** Pantalla a página completa con un icono y un mensaje (estados del enlace de casa). */
function PantallaAviso({ icono: Icono, tono, titulo, texto, testid, children }) {
  const fondo = tono === 'ok' ? 'bg-green-100' : tono === 'espera' ? 'bg-amber-50' : 'bg-red-50';
  const color = tono === 'ok' ? 'text-green-600' : tono === 'espera' ? 'text-amber-600' : 'text-red-600';
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F8FC] to-white flex items-center justify-center p-6">
      <Card className="w-full max-w-xl bg-white shadow-lg" data-testid={testid}>
        <CardContent className="text-center pt-10 pb-8 px-8">
          <div className={`mx-auto w-14 h-14 ${fondo} rounded-full flex items-center justify-center mb-4`}>
            <Icono className={`w-8 h-8 ${color}`} />
          </div>
          <h1 className="font-display text-2xl font-semibold text-[#13231E]">{titulo}</h1>
          {texto && <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{texto}</p>}
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Hoja LOPD para tablet (PUBLIC kiosk).
 * Phase 17.2: una sola pantalla scrolleable con la firma abajo.
 * Validación estricta de campos obligatorios — sin "Continuar/Atrás".
 */
export default function PublicLopdPage() {
  const clinicaDatos = useClinica();
  // Modo "casa": el paciente abre el enlace desde su móvil (no es el kiosco de
  // la clínica). Cambia el texto y quita el botón "siguiente paciente".
  const esCasa = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('casa') === '1';
  // El iPad con la cuenta de tablet (29/9/2026): «Cambiar de modo» vuelve al
  // selector sin contraseña. Nunca en el enlace de casa, que es del paciente.
  // Sin proveedor de sesión (la página sola) no hay tablet.
  const auth = useAuth();
  const navigate = useNavigate();
  const cuentaDeTablet = !esCasa && esCuentaDeTablet(auth?.user);
  const casaToken = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('t') || '')
    : '';
  // La cita para la que se mandó este enlace. Viaja en la URL y no dentro del
  // enlace (dirección, 2026-09-15): el enlace es uno por paciente y se reutiliza
  // mientras siga sin rellenar, así que lo que se guardase en él se lo comía
  // la siguiente cita que pasara por delante. Es lo único que decide si se le
  // piden las dos fotos; sin ella se piden, que es el lado seguro.
  const casaCita = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('cita') || '')
    : '';
  const urlEnlace = `/api/public/lopd/casa/${encodeURIComponent(casaToken)}`
    + (casaCita ? `?cita=${encodeURIComponent(casaCita)}` : '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const sigRef = useRef(null);
  // Estado inicial extraído como factory para poder resetear tras envío
  // (Phase 23.6 fix: el botón "Listo" debe limpiar y volver al formulario
  // en blanco, no navegar a otra ruta).
  const buildInitialForm = () => ({
    name: '',
    surname1: '',
    nif: '',
    birth_date: '',
    email: '',
    phone: '',
    address: { calle: '', poblacion: '', codigo_postal: '', pais: 'España' },
    how_known_us: '',
    consent_reminders: false,
    consent_commercial: false,
  });
  const [form, setForm] = useState(buildInitialForm);
  // Las opciones de «¿Cómo nos conoció?», del catálogo vigente (23/9/2026).
  // `versionOrigenes` sube si el servidor dice que la elegida ya no existe.
  const [versionOrigenes, setVersionOrigenes] = useState(0);
  const opcionesOrigen = useOpcionesComoNosConocio({ publico: true, version: versionOrigenes });
  // Cuestionario de salud (documento único LOPD+salud, 2026-07-26).
  const buildInitialSalud = () => ({
    alergias: null, alergias_text: '',
    medicacion: null, medicacion_text: '',
    hialuronico: null, hialuronico_text: '',
    cirugias: null, cirugias_text: '',
  });
  const [salud, setSalud] = useState(buildInitialSalud);
  const setS = (k, v) => setSalud((p) => ({ ...p, [k]: v }));
  // vista → { subiendo, url (vista previa local), ok } — solo enlace de casa
  const [fotos, setFotos] = useState({ frontal: null, oblicua: null });
  // ¿Le pedimos las fotos? Lo contesta el servidor con el tipo de la cita que
  // va en la URL. Se empieza en `true` y solo un `pide_fotos: false` explícito
  // las quita: si el servidor es de antes del cambio y no manda el campo, el
  // paciente ve lo de siempre en vez de quedarse sin poder subirlas.
  const [pideFotos, setPideFotos] = useState(true);
  // Error de la última foto, pintado dentro del recuadro «Tus dos fotos»
  // además del toast (revisión de enlaces 2026-09-07).
  const [fotoError, setFotoError] = useState('');

  // Estado del enlace de casa, comprobado ANTES de enseñar el formulario
  // (revisión de enlaces 2026-09-07): con un token caducado, ya usado o
  // inválido el paciente rellenaba todo (datos, cuestionario, dos fotos y
  // firma) y se enteraba al enviar; y tras enviar con éxito, al reabrir el
  // enlace veía el formulario en blanco. Estados: cargando | pendiente |
  // completado | caducado | invalido | incompleto (422: se cortó al copiarlo,
  // 23/9/2026) | espera (429) | error (no se pudo comprobar).
  // `marca` (administración, 28/10/2026): la marca con la que sale ESTE
  // enlace, congelada en él por el servidor con la regla de todos los
  // documentos (el médico de la cita del paciente: el titular, el formato de
  // la clínica; otro médico, el de su marca). Trae nombre, lema, dirección,
  // teléfono, web, colores y, si lo hay, por dónde pedir el logotipo. Sin
  // ella (el iPad, un servidor de antes), lo de Configuración, como siempre.
  const [enlace, setEnlace] = useState(() => ({ estado: esCasa ? 'cargando' : 'pendiente', nombre: '', marca: null }));
  const [intentoEnlace, setIntentoEnlace] = useState(0);
  // El 429 al abrir el enlace, para decir cuánto esperar (23/9/2026).
  const [falloEnlace, setFalloEnlace] = useState(null);
  // Por qué no se ha guardado el último envío, EN PANTALLA junto al botón
  // (23/9/2026). El 409 «El documento no coincide con la ficha…» era un
  // toast de 4 segundos: la paciente no sabía qué había pasado ni qué hacer,
  // y no podía terminar. {titulo, motivo, ayuda}; se va al volver a enviar.
  const [aviso, setAviso] = useState(null);

  // Al abrir la página: los avisos de un intento anterior en esta pestaña que
  // se quedaron sin respuesta (ver `avisarIntentoLopd`).
  useEffect(() => { reenviarAvisosPendientes(); }, []);

  useEffect(() => {
    if (!esCasa || !casaToken) return undefined;
    let vivo = true;
    setEnlace({ estado: 'cargando', nombre: '' });
    api.get(urlEnlace)
      .then((r) => {
        if (!vivo) return;
        const estado = r.data?.estado;
        setPideFotos(r.data?.pide_fotos !== false);
        setEnlace({
          estado: ['pendiente', 'completado', 'caducado'].includes(estado) ? estado : 'invalido',
          nombre: r.data?.nombre || '',
          marca: marcaDelEnlace(r.data?.marca),
        });
      })
      .catch((e) => {
        if (!vivo) return;
        // 404 = el token no existe. Cualquier otra cosa (red caída, 5xx) no
        // dice nada del enlace: se ofrece reintentar.
        const status = e?.response?.status;
        // 422 = token más corto de lo que exige el servidor: se cortó al
        // copiarlo. No es un fallo de red (verificación 2026-09-07), pero
        // tampoco un enlace «no válido»: es un enlace INCOMPLETO, y se dice
        // así, como en el cuestionario de salud y en confirmar cita
        // (23/9/2026). Reintentar no lo completa: que abra el del mensaje.
        // 429 = el freno de peticiones: ni enlace malo ni avería, esperar y
        // saber cuánto (23/9/2026).
        setFalloEnlace(e);
        let estado = 'error';
        if (status === 404 || status === 410) estado = 'invalido';
        else if (status === 422) estado = 'incompleto';
        else if (status === 429) estado = 'espera';
        setEnlace({ estado, nombre: '' });
      });
    return () => { vivo = false; };
  }, [esCasa, casaToken, urlEnlace, intentoEnlace]);

  const subirFoto = async (vista, archivo) => {
    if (!archivo) return;
    const url = URL.createObjectURL(archivo);
    setFotoError('');
    setFotos((prev) => ({ ...prev, [vista]: { subiendo: true, url, ok: false } }));
    try {
      const fd = new FormData();
      fd.append('file', archivo);
      fd.append('vista', vista);
      await api.post(`/api/public/lopd/casa/${encodeURIComponent(casaToken)}/foto`, fd);
      setFotos((prev) => ({ ...prev, [vista]: { subiendo: false, url, ok: true } }));
    } catch (e) {
      setFotos((prev) => ({ ...prev, [vista]: null }));
      // 429: cuándo volver a intentarlo. Sin respuesta o 5xx: no se sabe si
      // llegó; volver a subirla es seguro, el servidor sustituye la anterior
      // (23/9/2026, igual que en el cuestionario de salud).
      let msg;
      if (e?.response?.status === 429) {
        msg = `Hay mucho movimiento desde tu conexión. Vuelve a intentarlo ${cuandoReintentar(e)}.`;
      } else if (e?.response?.status === 413 || e?.response?.status === 503) {
        // Foto demasiado grande (24/9/2026) o el servidor ocupado con otras:
        // lo que dice el servidor, que va escrito para el paciente; y si el
        // 413 lo corta antes el proxy (sin texto), uno de tú.
        msg = fotoRechazada(e);
      } else if (respuestaPerdida(e)) {
        msg = 'No hemos podido confirmar que la foto se haya subido. Comprueba tu conexión y '
          + 'vuelve a subirla (si ya había llegado, se sustituye).';
      } else {
        msg = mensajeError(e, 'No se pudo subir la foto; inténtalo de nuevo.');
      }
      setFotoError(msg);
      toast.error(msg);
    }
  };

  // Cuántos avisos se han mandado para ESTE paciente (ver MAX_AVISOS_POR_PAGINA).
  const avisosMandados = useRef(0);

  const resetForm = () => {
    // Siguiente paciente en la tablet (la página no se recarga): su tope de
    // avisos empieza de cero, o el iPad se quedaría mudo tras veinte.
    avisosMandados.current = 0;
    setResult(null);
    setForm(buildInitialForm());
    setSalud(buildInitialSalud());
    setTouched({});
    setSubmitAttempted(false);
    sigRef.current?.clear();
    window.scrollTo({ top: 0, behavior: desplazamiento() });
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setA = (k, v) => setForm(f => ({ ...f, address: { ...f.address, [k]: v } }));
  const touch = (k) => setTouched(t => ({ ...t, [k]: true }));

  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = 'Obligatorio';
    if (!form.surname1.trim()) e.surname1 = 'Obligatorio';
    if (!form.nif.trim()) e.nif = 'Obligatorio';
    if (!form.birth_date) e.birth_date = 'Obligatorio';
    if (!form.address.calle.trim()) e.calle = 'Obligatorio';
    if (!form.address.codigo_postal.trim()) e.codigo_postal = 'Obligatorio';
    if (!form.address.poblacion.trim()) e.poblacion = 'Obligatorio';
    if (!form.address.pais.trim()) e.pais = 'Obligatorio';
    if (!form.phone.trim()) e.phone = 'Obligatorio';
    // Lo rellena el propio paciente: se le dice qué hacer si no se acuerda.
    if (!form.how_known_us.trim()) e.how_known_us = `Elija una opción. Si no lo recuerda, elija «${NO_LO_RECUERDA}».`;
    if (!form.consent_reminders) e.consent_reminders = 'Debes aceptarlo para continuar';
    return e;
  }, [form]);

  const showErr = (k) => (submitAttempted || touched[k]) ? errors[k] : null;

  const computeAge = (iso) => {
    if (!iso) return '';
    try {
      const b = new Date(iso);
      if (Number.isNaN(b.getTime())) return '';
      const today = new Date();
      let age = today.getFullYear() - b.getFullYear();
      const m = today.getMonth() - b.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
      return age >= 0 && age < 130 ? String(age) : '';
    } catch { return ''; }
  };
  const age = computeAge(form.birth_date);

  const sigEmpty = () => !sigRef.current || sigRef.current.isEmpty();

  /** Le cuenta al servidor un error que el paciente ha visto al pulsar
   *  «Firmar y enviar» y que el servidor no ha podido apuntar. Fuego y
   *  olvido: no se espera y no se enseña nada si falla (ver
   *  `avisarIntentoLopd`). */
  const avisarAlServidor = (tipo, texto, codigo) => {
    if (avisosMandados.current >= MAX_AVISOS_POR_PAGINA) return;
    avisosMandados.current += 1;
    const url = esCasa
      ? `/api/public/lopd/casa/${encodeURIComponent(casaToken)}/intento-fallido`
      : '/api/public/lopd/tablet/intento-fallido';
    try {
      avisarIntentoLopd(url, cuerpoDelAviso(tipo, texto, codigo));
    } catch {
      /* el aviso nunca molesta al paciente */
    }
  };

  /** Un freno de la propia página: el aviso al paciente y, al servidor, lo
   *  mismo que ha visto (y, si son campos, cuáles). */
  const frenar = (mensaje, detalle = '') => {
    toast.error(mensaje);
    avisarAlServidor('validacion_cliente', detalle ? `${mensaje} — ${detalle}` : mensaje);
  };

  /** Un error tras mandar el formulario: si el servidor no lo apuntó ya, se
   *  le cuenta lo que ha visto el paciente (`tipoDelFallo`): sin respuesta,
   *  `sin_respuesta`; con una respuesta de error que el servidor no apuntó
   *  (el proxy con el servidor caído, o el servidor no pudo guardar su
   *  apunte), `error_servidor`; y si falló la propia página antes de mandar
   *  nada, `error_pagina` con el nombre del fallo (para saber que es del
   *  programa y no de un dato). Los 403/400 de la frontera de Origin/Referer
   *  del servidor NO acaban apuntados: este aviso choca con la misma
   *  frontera. */
  const avisarSiNoLoTiene = (e, texto, tipo = null) => {
    if (yaLoApuntoElServidor(e)) return;
    const status = e?.response?.status;
    let elTipo = tipo;
    let elTexto = texto;
    if (!elTipo) {
      elTipo = tipoDelFallo(e);
      if (elTipo === 'error_pagina') elTexto = `${texto} (${e?.name || 'Error'} en la página)`;
    }
    avisarAlServidor(elTipo, elTexto, Number.isInteger(status) ? status : undefined);
  };

  const submit = async () => {
    setSubmitAttempted(true);
    if (Object.keys(errors).length > 0) {
      frenar('Faltan campos obligatorios marcados con *',
        `faltan: ${Object.keys(errors).map((k) => ETIQUETA_CAMPO[k] || k).join(', ')}`);
      const firstField = document.querySelector('[data-field-error="true"]');
      if (firstField) firstField.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      return;
    }
    // Cuestionario de salud: sí/no obligatorio y detalle obligatorio con sí.
    const saludBloques = [
      ['alergias', 'alergias o intolerancias a medicamentos'],
      ['medicacion', 'medicación habitual'],
      ['hialuronico', 'ácido hialurónico'],
      ['cirugias', 'cirugías previas'],
    ];
    for (const [campo, label] of saludBloques) {
      const irSalud = () => document.getElementById('salud-box')
        ?.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      if (salud[campo] === null) {
        frenar('Cuestionario de salud: responde sí o no a todas las preguntas');
        irSalud();
        return;
      }
      if (salud[campo] === true && !(salud[`${campo}_text`] || '').trim()) {
        frenar(`Cuestionario de salud: falta el detalle de ${label}`);
        irSalud();
        return;
      }
    }
    // Faltar fotos solo frena a quien se las hemos pedido: en una revisión
    // sucesiva o una intervención no se piden, y antes el envío se quedaba
    // bloqueado ahí para siempre (administración, 2026-09-15).
    if (esCasa && pideFotos && (!fotos.frontal?.ok || !fotos.oblicua?.ok)) {
      frenar('Faltan tus dos fotos (de frente y girada 45°).');
      document.getElementById('fotos-box')?.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      return;
    }
    if (esCasa && !(salud.mejorar || '').trim()) {
      frenar('Cuéntanos brevemente qué te gustaría mejorar o tratar.');
      document.getElementById('fotos-box')?.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      return;
    }
    if (sigEmpty()) {
      frenar('Por favor, firma antes de enviar');
      const sigBox = document.getElementById('signature-box');
      if (sigBox) sigBox.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      return;
    }
    setBusy(true);
    setAviso(null);
    try {
      const signature_b64 = sigRef.current.getCanvas().toDataURL('image/png');
      const payload = {
        ...form,
        nif: form.nif.trim().toUpperCase(),
        // El backend ignora el campo `surname2` (no obligatorio en este flujo)
        surname2: '',
        // Para mantener compatibilidad con el endpoint actual mapeamos
        // `consent_reminders` también a `consent_health_info` (es el mismo
        // consentimiento desde el punto de vista del paciente).
        consent_health_info: form.consent_reminders,
        signature_b64,
        // Documento único: el cuestionario de salud viaja con la LOPD y se
        // vuelca a la ficha con la misma firma.
        salud: { ...salud },
        // El backend necesita saber si esto viene del móvil del paciente para
        // no darle por llegado a la clínica (luz verde).
        casa: esCasa,
        public_token: esCasa ? casaToken : undefined,
      };
      const res = await api.post('/api/public/lopd/submit', payload);
      setResult(res.data);
      // Una petición buena: los avisos que se quedaron sin respuesta, ahora.
      reenviarAvisosPendientes();
    } catch (e) {
      // 410 en modo casa: el backend distingue «ya fue utilizado» (un envío
      // hecho o en curso) de «ha caducado».
      const detail = String(e?.response?.data?.detail || '');
      if (esCasa && e?.response?.status === 410 && /utilizad/i.test(detail)) {
        /* NO SE DEDUCE DEL ERROR QUE SU FORMULARIO HAYA LLEGADO (dirección,
         * 2026-09-21). Aquí se daba por hecho que un 410 «ya fue utilizado»
         * significaba que el envío anterior SÍ se guardó, y se le enseñaba
         * «Formulario recibido». Con una reserva colgada —un envío que murió
         * a medias y dejó el enlace en «procesando»— eso era falso: la
         * paciente rellenaba diez minutos, firmaba, y se le decía que su
         * consentimiento había llegado cuando no había llegado nada, ni la
         * LOPD, ni el cuestionario con sus alergias, ni las fotos. Se iba
         * tranquila y no volvía a intentarlo.
         *
         * El agujero grande está tapado en el servidor (la reserva caduca a
         * los diez minutos, `documents_router._reserva_vencida`); esto es la
         * otra mitad y vale para lo que quede: dos envíos a la vez en los que
         * el que ganó la carrera revienta después. Antes de decirle a nadie
         * que su formulario llegó, SE PREGUNTA. Si el servidor no lo confirma
         * —dice que sigue pendiente, o no se le puede preguntar— se le dice
         * que no ha salido y se le deja reintentar con todo lo que escribió
         * delante, que sigue en pantalla. */
        let confirmado = false;
        try {
          const r = await api.get(urlEnlace);
          confirmado = r?.data?.estado === 'completado';
          if (r?.data?.estado === 'caducado') {
            setEnlace((prev) => ({ ...prev, estado: 'caducado' }));
            avisarSiNoLoTiene(e, 'Enlace caducado');
            return;
          }
        } catch {
          // Tampoco se puede preguntar (23/9/2026): entonces no se sabe si
          // llegó, y «no hemos podido guardar» sería otro dato inventado.
          avisoNoSeSabe();
          avisarSiNoLoTiene(e, TITULO_NO_SE_SABE, 'no_se_sabe');
          return;
        }
        if (confirmado) {
          setEnlace((prev) => ({ ...prev, estado: 'completado' }));
          return;
        }
        const noGuardado = 'No hemos podido guardar tu formulario. No cierres esta '
          + 'página y vuelve a pulsar «Firmar y enviar».';
        toast.error(noGuardado);
        avisarSiNoLoTiene(e, noGuardado);
        return;
      }
      if (esCasa && e?.response?.status === 410 && /caducad/i.test(detail)) {
        setEnlace((prev) => ({ ...prev, estado: 'caducado' }));
        avisarSiNoLoTiene(e, 'Enlace caducado');
        return;
      }
      // Rechazado porque lo escrito no casa con su ficha (409 del servidor:
      // «El documento/nombre no coincide con la ficha…»). Se queda en
      // pantalla con el motivo, qué revisar y a quién llamar (23/9/2026). El
      // teléfono cuenta: si coincide con el de su ficha, el servidor la da
      // por identificada aunque el DNI o el nombre estén escritos de otra
      // forma (documents_router._datos_contradicen_ficha).
      if (esCasa && e?.response?.status === 409) {
        mostrarAviso({
          titulo: 'No hemos podido guardar su formulario',
          motivo: detail ? `${detail.replace(/\.$/, '')}.` : '',
          ayuda: 'Revise que el DNI/NIE, el nombre y el teléfono sean los mismos que dio en la '
            + 'clínica y vuelva a pulsar «Firmar y enviar». Si están bien escritos, '
            + `${llamenosClinica} y lo solucionamos.`,
        });
        avisarSiNoLoTiene(e, detail || 'No hemos podido guardar su formulario');
        return;
      }
      // Lo que eligió en «¿Cómo nos conoció?» ya no está en la lista: la
      // clínica la cambió con la página abierta (el iPad no recarga entre
      // paciente y paciente). Se vuelve a pedir la lista, se deja el campo
      // por elegir y el porqué se queda en pantalla, no en un aviso que se
      // va a los cuatro segundos (23/9/2026).
      if (esOrigenQueYaNoExiste(e)) {
        setVersionOrigenes((v) => v + 1);
        setF('how_known_us', '');
        touch('how_known_us');
        mostrarAviso({
          titulo: 'Vuelva a elegir cómo nos conoció',
          motivo: detail ? `${detail.replace(/\.$/, '')}.` : '',
          ayuda: 'La lista de opciones se ha actualizado. Elija una de nuevo (si no lo '
            + `recuerda, «${NO_LO_RECUERDA}») y vuelva a pulsar «Firmar y enviar».`,
        });
        avisarSiNoLoTiene(e, detail || 'Vuelva a elegir cómo nos conoció');
        return;
      }
      if (e?.response?.status === 429) {
        mostrarAviso({
          titulo: 'Hay mucho movimiento en este momento',
          motivo: '',
          ayuda: `Vuelva a pulsar «Firmar y enviar» ${cuandoReintentar(e)}. Si no puede esperar, `
            + `${llamenosClinica}.`,
        });
        avisarSiNoLoTiene(e, 'Hay mucho movimiento en este momento');
        return;
      }
      // Sin respuesta o 5xx: pudo guardarse. Antes de decir nada se le
      // pregunta al servidor (23/9/2026); ver `comprobarEnvio`.
      if (esCasa && respuestaPerdida(e)) {
        await comprobarEnvio(e);
        return;
      }
      const mensaje = mensajeError(e, 'No se pudo enviar. Inténtelo de nuevo');
      toast.error(mensaje);
      avisarSiNoLoTiene(e, mensaje);
    } finally {
      setBusy(false);
    }
  };

  // El de Configuración, con el formato de los mensajes; si no se ha podido
  // leer (sin conexión tampoco llega), el último que este dispositivo leyó
  // bien de esta clínica; y si no hay ninguno, ninguno (23/9/2026): aquí
  // había un número fijo, el de CJO, y este build sirve a otras clínicas.
  // Se vuelve a pedir tras cada comprobación del enlace (el `pedir` pasa a
  // falso mientras carga): así «Reintentar» también recupera el teléfono si
  // la Configuración falló la primera vez (23/9/2026, lib/clinica).
  // Con marca en el enlace (28/10/2026), el teléfono de la marca manda sobre
  // el de Configuración: también en los «llame a la clínica (tel)».
  const marca = enlace.marca;
  const telefonoClinica = useTelefonoClinica(marca?.telefono || clinicaDatos.phone, enlace.estado !== 'cargando');
  const llamanosClinica = aQuienLlamar(telefonoClinica);
  const llamenosClinica = aQuienLlamar(telefonoClinica, { usted: true });

  /** Pone el aviso junto al botón de enviar y lo lleva a la vista. */
  function mostrarAviso(datos) {
    setAviso(datos);
    setTimeout(() => {
      document.getElementById('aviso-envio')
        ?.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
    }, 0);
  }

  /**
   * El envío de casa se quedó sin contestación (o con un 5xx del proxy):
   * pudo guardarse. Se pregunta por el enlace: completado → «recibido»;
   * caducado → lo que es; pendiente → que no ha llegado, para volver a
   * enviarlo; y si ni eso se puede preguntar, que no se sabe. Nunca un
   * «no se pudo enviar» que no se sabe (23/9/2026).
   */
  async function comprobarEnvio(e) {
    let estado = null;
    try {
      const r = await api.get(urlEnlace);
      estado = r?.data?.estado || null;
      // Ha vuelto la conexión: lo que se quedó sin respuesta, ahora.
      reenviarAvisosPendientes();
    } catch {
      estado = null;
    }
    if (estado === 'completado' || estado === 'caducado') {
      setEnlace((prev) => ({ ...prev, estado }));
      // «Recibido» no es un error; «caducado», sí (lo que ha visto).
      if (estado === 'caducado') avisarSiNoLoTiene(e, 'Enlace caducado');
      return;
    }
    if (estado === 'pendiente') {
      const titulo = 'Su formulario no ha llegado a la clínica';
      mostrarAviso({
        titulo,
        motivo: '',
        ayuda: 'Compruebe su conexión y vuelva a pulsar «Firmar y enviar»: lo que ha escrito '
          + `sigue aquí. Si sigue sin funcionar, ${llamenosClinica}.`,
      });
      avisarSiNoLoTiene(e, titulo);
      return;
    }
    avisoNoSeSabe();
    avisarSiNoLoTiene(e, TITULO_NO_SE_SABE, 'no_se_sabe');
  }

  /** Ni el envío ni la pregunta han tenido contestación: no se sabe si llegó.
   *  Reenviar es seguro: si ya había llegado, el servidor contesta «ya
   *  utilizado» y se comprueba de nuevo. */
  function avisoNoSeSabe() {
    mostrarAviso({
      titulo: TITULO_NO_SE_SABE,
      motivo: '',
      ayuda: 'Compruebe su conexión y vuelva a pulsar «Firmar y enviar»: si ya había llegado, '
        + `se lo diremos. Si sigue sin funcionar, ${llamenosClinica}.`,
    });
  }

  // Sin token (`?casa=1` sin `t`) o con uno más corto de lo que exige el
  // servidor (422): el enlace se cortó al copiarlo. Es INCOMPLETO, no «no
  // válido», como en el cuestionario de salud y en confirmar cita (23/9/2026).
  if (esCasa && (!casaToken || enlace.estado === 'incompleto')) {
    return (
      <PantallaAviso icono={AlertCircle} tono="error" titulo="Este enlace está incompleto" testid="lopd-casa-incompleto"
        texto={`Ábrelo desde el mensaje completo que te enviamos. Si no lo encuentras, ${llamanosClinica} y te lo enviamos de nuevo.`} />
    );
  }
  if (esCasa && enlace.estado === 'cargando') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F4F8FC] to-white flex items-center justify-center p-6 text-muted-foreground" data-testid="lopd-casa-cargando">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Comprobando su enlace…
      </div>
    );
  }
  if (esCasa && enlace.estado === 'completado') {
    return (
      <PantallaAviso icono={CheckCircle2} tono="ok" titulo="Formulario recibido" testid="lopd-casa-recibido"
        texto="Ya hemos recibido su formulario; no hace falta volver a enviarlo." />
    );
  }
  if (esCasa && enlace.estado === 'caducado') {
    return (
      <PantallaAviso icono={Clock} tono="espera" titulo="Enlace caducado" testid="lopd-casa-caducado"
        texto={telefonoClinica
          ? `Este enlace ha caducado: pida uno nuevo a la clínica (${telefonoClinica}).`
          : 'Este enlace ha caducado: pida uno nuevo a la clínica.'} />
    );
  }
  if (esCasa && enlace.estado === 'invalido') {
    return (
      <PantallaAviso icono={AlertCircle} tono="error" titulo="Este enlace no es válido" testid="lopd-casa-invalido"
        texto={`Pide a la clínica que te envíe de nuevo el enlace seguro del formulario: ${llamanosClinica}.`} />
    );
  }
  // Sin conexión o avería (error) y el freno de peticiones (espera): el
  // enlace sigue siendo suyo; reintentar y, si no, llamar (23/9/2026).
  if (esCasa && (enlace.estado === 'error' || enlace.estado === 'espera')) {
    const espera = enlace.estado === 'espera';
    return (
      <PantallaAviso icono={espera ? Clock : AlertCircle} tono="espera"
        titulo={espera ? 'Hay mucho movimiento en este momento' : 'No hemos podido comprobar su enlace'}
        testid={espera ? 'lopd-casa-espera' : 'lopd-casa-error'}
        texto={espera
          ? `Vuelva a intentarlo ${cuandoReintentar(falloEnlace)}. Su enlace sigue siendo válido.`
          : 'Compruebe su conexión a internet e inténtelo de nuevo.'}>
        <Button variant="outline" className="mt-5" onClick={() => setIntentoEnlace((n) => n + 1)} data-testid="lopd-casa-reintentar">
          <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
        </Button>
        <p className="text-sm text-muted-foreground mt-4">
          Si sigue sin funcionar, {llamenosClinica}.
        </p>
      </PantallaAviso>
    );
  }

  // ----- Vista de confirmación tras envío -----
  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2E6E5E] to-[#2E6E5E] flex items-center justify-center p-6">
        <Card className="w-full max-w-xl bg-white shadow-xl">
          <CardContent className="text-center pt-10 pb-8 px-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-[#13231E]">¡Gracias, {result.patient_name}!</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              {esCasa
                ? 'Hemos recibido tus datos y tu firma correctamente. Ya puedes cerrar esta página — no hace falta nada más.'
                : 'Tu consentimiento ha quedado registrado correctamente.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  const downloadToken = result.pdf_token || (esCasa ? casaToken : '');
                  const qs = downloadToken ? `?token=${encodeURIComponent(downloadToken)}` : '';
                  window.open(`${BACKEND_URL}/api/public/lopd/${result.lopd_id}/pdf${qs}`, '_blank');
                }}
              >
                <Download className="w-4 h-4 mr-2" /> Descargar mi copia
              </Button>
              {!esCasa && (
                <Button
                  onClick={resetForm}
                  className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                  data-testid="public-lopd-done-btn"
                >
                  Listo — siguiente paciente
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F8FC] to-white">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* La marca del enlace (28/10/2026): su logotipo, su nombre y su
                lema con sus colores. Sin marca, el logo y el nombre de
                Configuración; sin logo, nada: el nombre va al lado. */}
            {marca
              ? <LogoMarca marca={marca} className="h-9 w-9 rounded object-contain" />
              : <LogoClinica className="h-9 w-9 rounded object-contain" />}
            <div>
              <div
                className="font-display font-semibold text-[#13231E] leading-tight"
                style={marca?.color ? { color: marca.color } : undefined}
                data-testid="lopd-cabecera-nombre"
              >
                {marca ? marca.nombre : clinicaDatos.clinic_name}
              </div>
              {marca?.lema ? (
                <div
                  className="text-[11px] italic text-muted-foreground leading-tight"
                  style={marca.color_punto ? { color: marca.color_punto } : undefined}
                  data-testid="lopd-cabecera-lema"
                >
                  {marca.lema}
                </div>
              ) : null}
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Consentimiento LOPD</div>
            </div>
          </div>
          {cuentaDeTablet ? (
            <button
              type="button"
              onClick={() => { olvidarModoTablet(); navigate(RUTA_SELECTOR_TABLET, { replace: true }); }}
              className="text-xs text-muted-foreground hover:text-[#13231E] px-2 py-1"
              data-testid="cambiar-modo-tablet"
            >
              Cambiar de modo
            </button>
          ) : (
            <Link to="/" className="text-xs text-muted-foreground hover:text-[#13231E] hidden sm:block">Acceso interno</Link>
          )}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        <Card className="shadow-md">
          <CardContent className="pt-6 pb-8 sm:px-8 px-4 space-y-7">
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[#13231E]" data-testid="public-lopd-titulo">
                {esCasa && enlace.nombre ? `Hola, ${enlace.nombre}` : 'Bienvenido/a a la clínica'}
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                Rellena los datos y firma al final para autorizar el tratamiento de tus datos personales.
                Los campos marcados con <span className="text-red-600 font-semibold">*</span> son obligatorios.
              </p>
            </div>

            {/* DATOS PERSONALES */}
            <section>
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Datos personales</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div data-field-error={!!showErr('name')}>
                  <Label>Nombre<Asterisk /></Label>
                  <Input className="text-base h-11" maxLength={MAX.name} value={form.name} onChange={(e) => setF('name', e.target.value)} onBlur={() => touch('name')} data-testid="public-lopd-name" />
                  <FieldError msg={showErr('name')} />
                </div>
                <div data-field-error={!!showErr('surname1')}>
                  <Label>Apellido(s)<Asterisk /></Label>
                  <Input className="text-base h-11" maxLength={MAX.surname1} value={form.surname1} onChange={(e) => setF('surname1', e.target.value)} onBlur={() => touch('surname1')} placeholder="Apellido o ambos apellidos" data-testid="public-lopd-surname1" />
                  <FieldError msg={showErr('surname1')} />
                </div>
                <div data-field-error={!!showErr('nif')}>
                  <Label>DNI / NIE / Pasaporte<Asterisk /></Label>
                  <Input className="text-base h-11 uppercase" maxLength={MAX.nif} value={form.nif} onChange={(e) => setF('nif', e.target.value.toUpperCase())} onBlur={() => touch('nif')} data-testid="public-lopd-nif" />
                  <FieldError msg={showErr('nif')} />
                </div>
                <div data-field-error={!!showErr('birth_date')}>
                  <Label>Fecha de nacimiento{age && <span className="text-[#2E6E5E] ml-1 text-xs">({age} años)</span>}<Asterisk /></Label>
                  <BirthDatePicker
                    value={form.birth_date}
                    onChange={(v) => setF('birth_date', v)}
                    onBlur={() => touch('birth_date')}
                    hasError={!!showErr('birth_date')}
                  />
                  <FieldError msg={showErr('birth_date')} />
                </div>
                <div data-field-error={!!showErr('phone')}>
                  <Label>Teléfono<Asterisk /></Label>
                  <Input type="tel" inputMode="tel" className="text-base h-11" maxLength={MAX.phone} value={form.phone} onChange={(e) => setF('phone', e.target.value)} onBlur={() => touch('phone')} data-testid="public-lopd-phone" />
                  <p className="text-xs text-muted-foreground mt-1">
                    Escriba el <strong>mismo teléfono</strong> con el que contactó con la
                    clínica o se gestionó su cita: nos sirve para localizar su ficha.
                  </p>
                  <FieldError msg={showErr('phone')} />
                </div>
                <div>
                  <Label>Email <span className="text-xs text-muted-foreground">(opcional)</span></Label>
                  <Input type="email" inputMode="email" className="text-base h-11" maxLength={MAX.email} value={form.email} onChange={(e) => setF('email', e.target.value)} data-testid="public-lopd-email" />
                </div>
              </div>
            </section>

            {/* DIRECCIÓN */}
            <section>
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Dirección</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-3" data-field-error={!!showErr('calle')}>
                  <Label>Calle y número<Asterisk /></Label>
                  <Input className="text-base h-11" maxLength={MAX.calle} value={form.address.calle} onChange={(e) => setA('calle', e.target.value)} onBlur={() => touch('calle')} data-testid="public-lopd-calle" />
                  <FieldError msg={showErr('calle')} />
                </div>
                <div data-field-error={!!showErr('codigo_postal')}>
                  <Label>Código Postal<Asterisk /></Label>
                  <Input className="text-base h-11" inputMode="numeric" maxLength={MAX.codigo_postal} value={form.address.codigo_postal} onChange={(e) => setA('codigo_postal', e.target.value)} onBlur={() => touch('codigo_postal')} data-testid="public-lopd-cp" />
                  <FieldError msg={showErr('codigo_postal')} />
                </div>
                <div data-field-error={!!showErr('poblacion')}>
                  <Label>Ciudad<Asterisk /></Label>
                  <Input className="text-base h-11" maxLength={MAX.poblacion} value={form.address.poblacion} onChange={(e) => setA('poblacion', e.target.value)} onBlur={() => touch('poblacion')} data-testid="public-lopd-poblacion" />
                  <FieldError msg={showErr('poblacion')} />
                </div>
                <div data-field-error={!!showErr('pais')}>
                  <Label>País<Asterisk /></Label>
                  <select
                    value={form.address.pais}
                    onChange={(e) => { setA('pais', e.target.value); touch('pais'); }}
                    onBlur={() => touch('pais')}
                    className={clsSelectNativo(!!showErr('pais'))}
                    aria-label="País"
                    data-testid="public-lopd-pais"
                  >
                    <option value="" disabled>País</option>
                    {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <FieldError msg={showErr('pais')} />
                </div>
              </div>
            </section>

            {/* CÓMO NOS CONOCIÓ */}
            <section>
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">¿Cómo nos conoció?</h2>
              <div data-field-error={!!showErr('how_known_us')}>
                <Label>Elija una opción<Asterisk /></Label>
                <p className="text-xs text-muted-foreground mb-1">Si no lo recuerda, elija «{NO_LO_RECUERDA}».</p>
                <select
                  value={form.how_known_us}
                  onChange={(e) => { setF('how_known_us', e.target.value); touch('how_known_us'); }}
                  onBlur={() => touch('how_known_us')}
                  className={clsSelectNativo(!!showErr('how_known_us'))}
                  aria-label="¿Cómo nos conoció?"
                  aria-required="true"
                  data-testid="public-lopd-how-known"
                >
                  <option value="" disabled>Elija…</option>
                  {opcionesOrigen.map((o) => {
                    const texto = etiquetaParaElPaciente(o);
                    return <option key={o} value={texto}>{texto}</option>;
                  })}
                </select>
                <FieldError msg={showErr('how_known_us')} />
              </div>
            </section>

            {/* CONSENTIMIENTOS */}
            <section>
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Consentimientos</h2>
              {/* El RESPONSABLE DEL TRATAMIENTO no cambia con la marca del
                  enlace (28/10/2026): es la razón social de Configuración, con
                  su dirección y su correo. Una marca no tiene razón social
                  propia (solo nombre, lema, dirección, teléfono, web y
                  colores), y quién responde de los datos es una cuestión legal,
                  no de formato: el mismo texto que en el PDF firmado. */}
              <p className="text-xs text-muted-foreground mb-3" data-testid="lopd-responsable">
                Responsable del tratamiento: <strong>{clinicaDatos.legal_entity || clinicaDatos.clinic_name}</strong>{clinicaDatos.address ? `, ${clinicaDatos.address}` : ''}.
                Finalidad: prestación de servicios médicos y comunicación con el paciente.
                Base jurídica: ejecución de contrato y consentimiento expreso.
                Derechos: acceso, rectificación, supresión, oposición, portabilidad y limitación en
                <a className="underline ml-1" href={`mailto:${clinicaDatos.email}`}>{clinicaDatos.email}</a>.
              </p>
              <div className={`flex items-start gap-3 p-3 rounded-md border ${showErr('consent_reminders') ? 'border-red-400 bg-red-50/30' : 'border-border bg-[#F4F8FC]/50'}`} data-field-error={!!showErr('consent_reminders')}>
                <Checkbox
                  checked={form.consent_reminders}
                  onCheckedChange={(v) => setF('consent_reminders', !!v)}
                  className="mt-1"
                  data-testid="public-lopd-consent-reminders"
                />
                <div className="flex-1">
                  <Label className="text-sm font-medium leading-snug cursor-pointer" onClick={() => setF('consent_reminders', !form.consent_reminders)}>
                    Consiento el tratamiento de mis datos personales (incluidos los de salud) por {clinicaDatos.legal_entity || clinicaDatos.clinic_name} para la prestación de los servicios médicos solicitados, así como el envío de recordatorios de cita por teléfono, SMS o WhatsApp.<Asterisk />
                  </Label>
                </div>
              </div>
              <FieldError msg={showErr('consent_reminders')} />

              <div className="flex items-start gap-3 p-3 rounded-md border border-border bg-white mt-2">
                <Checkbox
                  checked={form.consent_commercial}
                  onCheckedChange={(v) => setF('consent_commercial', !!v)}
                  className="mt-1"
                  data-testid="public-lopd-consent-commercial"
                />
                <div className="flex-1">
                  <Label className="text-sm leading-snug cursor-pointer text-muted-foreground" onClick={() => setF('consent_commercial', !form.consent_commercial)}>
                    Acepto recibir información comercial sobre tratamientos, novedades y promociones de la clínica. <span className="text-[11px] italic">(Opcional, puedes revocarlo en cualquier momento)</span>
                  </Label>
                </div>
              </div>
            </section>

            {/* CUESTIONARIO DE SALUD (documento único, 2026-07-26) */}
            <section id="salud-box">
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Cuestionario de salud</h2>
              <p className="text-xs text-muted-foreground mb-2">
                Cuatro preguntas rápidas para que tu visita empiece con todo listo.
              </p>
              <div className="space-y-3">
                {[
                  { campo: 'alergias', texto: '¿Tienes alergia o intolerancia a algún medicamento?', detalle: '¿A qué medicamento(s)? *', ph: 'Ej.: penicilina, ibuprofeno…' },
                  { campo: 'medicacion', texto: '¿Tomas alguna medicación habitualmente?', detalle: '¿Qué medicación tomas? *', ph: 'Ej.: Sintrom, omeprazol 20 mg…' },
                  { campo: 'hialuronico', texto: '¿Te has puesto alguna vez ácido hialurónico en ojeras o pómulos?', detalle: '¿Dónde y cuándo? *', ph: 'Ej.: ojeras, hace 2 años en otra clínica…' },
                  { campo: 'cirugias', texto: '¿Has entrado alguna vez en quirófano?', detalle: '¿Qué cirugías? *', ph: 'Ej.: apendicitis en 2010, cesárea, blefaroplastia en 2020…' },
                ].map((b) => (
                  <div key={b.campo} className="border border-border rounded-lg p-3 space-y-2"
                    data-testid={`lopd-salud-${b.campo}`}>
                    <div className="text-sm font-medium text-[#13231E]">{b.texto}</div>
                    <div className="flex gap-2">
                      {[['Sí', true], ['No', false]].map(([lbl, val]) => (
                        <button key={lbl} type="button"
                          onClick={() => setS(b.campo, val)}
                          className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                            salud[b.campo] === val
                              ? 'bg-[#2E6E5E] border-[#2E6E5E] text-white'
                              : 'bg-white border-border text-muted-foreground hover:text-[#13231E]'
                          }`}
                          data-testid={`lopd-salud-${b.campo}-${lbl.toLowerCase()}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                    {salud[b.campo] === true && (
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">{b.detalle}</div>
                        <textarea
                          rows={2}
                          maxLength={MAX.salud_text}
                          value={salud[`${b.campo}_text`]}
                          onChange={(e) => setS(`${b.campo}_text`, e.target.value)}
                          placeholder={b.ph}
                          className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6E5E]/40"
                          data-testid={`lopd-salud-${b.campo}-detalle`}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* FOTOS + QUÉ MEJORAR — solo enlace de casa (la tablet no).
                Las fotos, además, solo a quien le tocan por su cita
                (`pide_fotos`); lo que le gustaría mejorar se pregunta
                siempre, porque es de la paciente y no de la cita. */}
            {esCasa && (
              <section id="fotos-box">
                {pideFotos && (<>
                <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Tus dos fotos</h2>
                <p className="text-xs text-muted-foreground mb-2">
                  Haznos llegar dos fotos como las de ejemplo: una de frente y otra
                  girada 45°. Buena luz, sin gafas y con el pelo retirado de la cara.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {FOTOS_CASA.map((f) => (
                    <div key={f.vista} className="space-y-1.5" data-testid={`lopd-foto-${f.vista}`}>
                      <div className="text-xs font-semibold text-[#13231E]">{f.titulo}</div>
                      <div className="relative rounded-md overflow-hidden border border-border bg-[#F2F2F0]">
                        <img src={fotos[f.vista]?.url || f.ejemplo} alt={f.titulo}
                          className="w-full aspect-[3/4] object-cover" />
                        {!fotos[f.vista]?.url && (
                          <span className="absolute top-1 left-1 rounded bg-black/55 text-white text-[10px] px-1.5 py-0.5">
                            EJEMPLO
                          </span>
                        )}
                        {fotos[f.vista]?.subiendo && (
                          <span className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-white animate-spin" />
                          </span>
                        )}
                        {fotos[f.vista]?.ok && (
                          <span className="absolute top-1 right-1 rounded-full bg-[#2E6E5E] p-1">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          </span>
                        )}
                      </div>
                      <label className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-xs font-medium cursor-pointer transition-colors ${
                        fotos[f.vista]?.ok
                          ? 'border-[#2E6E5E] text-[#2E6E5E] bg-[rgba(46,110,94,0.06)]'
                          : 'border-[#2E6E5E] bg-[#2E6E5E] text-white hover:bg-[#1E4A3F]'
                      }`}>
                        <Camera className="w-4 h-4" />
                        {fotos[f.vista]?.ok ? 'Cambiar mi foto' : 'Subir mi foto'}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => { subirFoto(f.vista, e.target.files?.[0]); e.target.value = ''; }}
                          data-testid={`lopd-foto-${f.vista}-input`} />
                      </label>
                      <p className="text-[10px] text-muted-foreground leading-tight">{f.ayuda}</p>
                    </div>
                  ))}
                </div>
                {fotoError && (
                  <p className="text-sm text-red-600 mb-3 flex items-start gap-1" data-testid="lopd-foto-error">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-[2px]" /> {fotoError}
                  </p>
                )}
                </>)}
                <div className="border border-border rounded-lg p-3 space-y-2" data-testid="lopd-mejorar">
                  <div className="text-sm font-medium text-[#13231E]">¿Qué te gustaría mejorar o tratar?</div>
                  <textarea rows={3} maxLength={MAX.mejorar} value={salud.mejorar || ''}
                    onChange={(e) => setS('mejorar', e.target.value)}
                    placeholder="Ej.: las bolsas de los ojos y el exceso de piel del párpado superior…"
                    className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6E5E]/40"
                    data-testid="lopd-mejorar-texto" />
                </div>
              </section>
            )}

            {/* FIRMA */}
            <section id="signature-box">
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Firma</h2>
              <p className="text-xs text-muted-foreground mb-2">
                Firma con el dedo o un lápiz táctil dentro del recuadro. Tu firma constituye prueba del consentimiento.
              </p>
              <FirmaDedo
                ref={sigRef}
                testid="public-lopd-signature"
                alturaCls="h-48 sm:h-56"
                derecha={<span className="text-xs text-muted-foreground">{new Date().toLocaleDateString('es-ES')}</span>}
              />
            </section>

            {/* SUBMIT */}
            <div className="pt-2">
              {/* Por qué no se guardó el último envío: se queda hasta el
                  siguiente, con qué hacer y a quién llamar (23/9/2026). */}
              {aviso && (
                <div id="aviso-envio" role="alert"
                  className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm space-y-1"
                  data-testid="lopd-envio-rechazado">
                  <p className="font-semibold text-red-700 flex items-start gap-1">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-[2px]" /> {aviso.titulo}
                  </p>
                  {aviso.motivo && <p className="text-[#13231E]">{aviso.motivo}</p>}
                  <p className="text-[#13231E]">{aviso.ayuda}</p>
                </div>
              )}
              <Button
                onClick={submit}
                disabled={busy}
                className="w-full h-14 text-base bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                data-testid="public-lopd-submit"
              >
                {busy ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando…</> : 'Firmar y enviar'}
              </Button>
              {submitAttempted && Object.keys(errors).length > 0 && (
                <p className="text-xs text-red-600 mt-2 text-center">Revisa los campos marcados con *</p>
              )}
            </div>
          </CardContent>
        </Card>
        <p className="text-center text-[11px] text-muted-foreground mt-4 mb-6" data-testid="lopd-pie">
          {marca ? pieMarca(marca) : pieClinica(clinicaDatos)}
        </p>
      </div>
    </div>
  );
}
